import { useState, useMemo, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../Store/hooks';
import { getRecordings } from '../../Store/StudentSlices/getRecordingsSlice';
import RecordingPlayer from './RecordingPlayer';
import RecordingSidebar from './RecordingSidebar';
import RecordingTranscript from './RecordingTranscript';

const RecordingsView = () => {
  const dispatch = useAppDispatch();
  const { recordings, loading, error } = useAppSelector((state) => state.getRecordings);
  const [activeRecordingId, setActiveRecordingId] = useState<string | null>(null);

  useEffect(() => {
    dispatch(getRecordings({ page: 1, perPage: 10 }));
  }, [dispatch]);

  // Set initial active recording when recordings are loaded
  useEffect(() => {
    if (recordings.length > 0 && !activeRecordingId) {
      setActiveRecordingId(recordings[0].id);
    }
  }, [recordings, activeRecordingId]);

  const activeRecording = useMemo(() => {
    return recordings.find((r) => r.id === activeRecordingId) || (recordings.length > 0 ? recordings[0] : null);
  }, [activeRecordingId, recordings]);

  if (loading && recordings.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center text-red-500">
        <p>Failed to load recordings. Please try again later.</p>
      </div>
    );
  }

  if (recordings.length === 0 && !loading) {
    return (
      <div className="flex h-full items-center justify-center text-slate-500">
        <p>No recordings available yet.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 h-full overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500 pb-4">
      {/* Left Column: Player and Past Sessions */}
      <div className="lg:col-span-5 flex flex-col gap-10 overflow-y-auto pr-4 custom-scrollbar">
        {activeRecording && <RecordingPlayer key={activeRecording.id} recording={activeRecording} />}
        <RecordingSidebar 
          recordings={recordings} 
          activeRecordingId={activeRecordingId || ''}
          onRecordingSelect={setActiveRecordingId}
        />
      </div>

      {/* Right Column: Transcript */}
      <div className="lg:col-span-7 h-full overflow-hidden">
        {activeRecording && <RecordingTranscript recording={activeRecording} />}
      </div>
    </div>
  );
};

export default RecordingsView;
